import React, { useState, useEffect } from 'react';
import { ReactComponent as WarningIcon } from 'img/icons/icon-warning.svg';
import { ReactComponent as InfoIcon } from 'img/icons/icon-info.svg';
import { ReactComponent as DangerIcon } from 'img/icons/icon-danger.svg';
import { ReactComponent as CloseIcon } from 'img/icons/icon-close-x.svg';
import { getLinkDataFromDato } from 'helpers/get-link-data';

type AlertBarProps = {
    id: string;
    modifierClass: string;
    type: string;
    headline: string;
    content: string;
    link?: Queries.LinkBlockFragment;
    startDate?: Date;
    endDate?: Date;
    isDismissable?: boolean;
    isGlobal?: boolean;
};

const getLinkMarkup = (link: Queries.LinkBlockFragment) => {
    const linkData = getLinkDataFromDato(link.link);

    if (linkData) {
        return (<div className="content"><a href={linkData.url} target={linkData.target}>{linkData.label ?? 'Learn More'}</a></div>);
    }
};

const AlertBar: React.FC<AlertBarProps> = ({ id, modifierClass, type = 'warning', headline, content, link, isDismissable = true, startDate, endDate, isGlobal = false }) => {
    // Alerts are initially invisible, until we know if it should be shown on after the page loads
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if the alert has been dismissed in localStorage, set to visible if not
        const dismissed = localStorage.getItem(`alert-dismissed-${id}`);
        if (!dismissed) {
            setIsVisible(true);
        }
    }, [id]); // Dependency array ensures this effect runs once per component instance

    const handleClose = () => {
        setIsVisible(false);
        // Mark the alert as dismissed in localStorage
        localStorage.setItem(`alert-dismissed-${id}`, 'true');
    };

    const currentDate = new Date();
    if ((startDate && currentDate < startDate) || (endDate && currentDate > endDate) || !isVisible) {
        return null;
    }

    let Icon;

    switch (type) {
        case 'warning':
            Icon = WarningIcon;
            break;
        case 'info':
            Icon = InfoIcon;
            break;
        case 'danger':
            Icon = DangerIcon;
            break;
        default:
            Icon = WarningIcon;
    }

    const globalAlertAttrs = isGlobal ? {role: 'region', 'aria-label': 'Site-Wide Alert'} : {};

    return (
        <div className={`alert-bar ${modifierClass}`} {...globalAlertAttrs}>
            <div className="container">
                <div className="alert-bar__icon">
                    <Icon />
                </div>
                <div className="alert-bar__content">
                    <div className="headline">{headline}</div>
                    {content && (
                        <div className="content" dangerouslySetInnerHTML={{ __html: content }}></div>
                    )}
                    {link && (
                        getLinkMarkup(link)
                    )}
                </div>
                {isDismissable && (
                    <button className="alert-bar__close js-close-alert" onClick={handleClose}>
                        <CloseIcon />
                    </button>
                )}
            </div>
        </div>
    );
};

export default AlertBar;